import { useQuery } from "@tanstack/react-query";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";

/**
 * Presentation picker feed for the `/run` page's Match Validator card
 * (issue #571). Wraps the orchestrator's `GET /api/presentations` list
 * endpoint — see `services/orchestrator/src/routes/presentation-review.ts`
 * (`presentationsRoutes` block) for the response shape.
 *
 * Terminal-verdict statuses (`approved`, `rejected`, `expired`,
 * `failed`) are excluded on the server; the four non-terminal statuses
 * (`draft`, `pending_review`, `finalised`, `sent`) show through.
 */

export type PresentationPickerStatus =
  | "draft"
  | "pending_review"
  | "finalised"
  | "sent";

export type MatchValidationStatus =
  | "pending"
  | "validated"
  | "flagged"
  | "incomplete";

export interface PresentationPickerRow {
  id: string;
  shortId: string;
  memoId: string;
  memoShortId: string;
  status: PresentationPickerStatus;
  matchValidationStatus: MatchValidationStatus;
  clientName: string | null;
  clientCompany: string | null;
  createdAt: string;
}

export interface PresentationsListResponse {
  items: PresentationPickerRow[];
}

/** List the most recent 50 non-terminal presentations for the picker
 *  dropdown. */
export function usePresentations() {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["presentations", "picker"],
    queryFn: ({ signal }) =>
      apiFetch<PresentationsListResponse>(client, "/api/presentations", {
        signal,
      }),
  });
}
